import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '../Icons';
import { useAlertSync } from '../../hooks/useAlertSync';
import { useAuthStore } from '../../store/authStore';
import type { PriceAlert } from '../../services/alertService';

interface AlertPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onNavigateToAlerts?: () => void;
}

export function AlertPanel({ isOpen, onClose, onNavigateToAlerts }: AlertPanelProps) {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuthStore();
  const {
    alerts,
    isLoading,
    isSyncing,
    refreshAlerts,
    deleteAlert,
  } = useAlertSync();

  const [hoveredAlertId, setHoveredAlertId] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    refreshAlerts();
    const interval = setInterval(refreshAlerts, 30000);
    return () => clearInterval(interval);
  }, [isOpen, refreshAlerts]);

  if (!isOpen) return null;

  // Only show triggered alerts and recent active alerts (last 24h)
  const now = new Date();
  const triggeredAlerts = alerts.filter(a => a.triggeredAt);
  const recentActiveAlerts = alerts
    .filter(a => a.enabled && !a.triggeredAt)
    .filter(a => {
      const created = new Date(a.createdAt);
      const diff = now.getTime() - created.getTime();
      return diff < 24 * 60 * 60 * 1000; // Last 24 hours
    })
    .slice(0, 3); // Show max 3 recent

  const allNotifications = [...triggeredAlerts, ...recentActiveAlerts];
  const hasTriggered = triggeredAlerts.length > 0;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    if (minutes < 1) return t('alerts.justNow', 'À l\'instant');
    if (minutes < 60) return `${minutes}min`;
    if (hours < 24) return `${hours}h`;
    if (days < 7) return `${days}j`;
    return date.toLocaleDateString();
  };

  const getConditionText = (alert: PriceAlert) => {
    if (alert.condition === 'above' && alert.targetPrice) {
      return `> $${alert.targetPrice.toLocaleString()}`;
    }
    if (alert.condition === 'below' && alert.targetPrice) {
      return `< $${alert.targetPrice.toLocaleString()}`;
    }
    if (alert.condition === 'change_percent' && alert.changePercent) {
      const sign = alert.changePercent > 0 ? '+' : '';
      return `${sign}${alert.changePercent}%`;
    }
    return '';
  };

  const handleViewAll = () => {
    onClose();
    onNavigateToAlerts?.();
  };

  const handleDismiss = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    await deleteAlert(id);
  };

  const stats = {
    active: alerts.filter(a => a.enabled && !a.triggeredAt).length,
    triggered: triggeredAlerts.length,
    total: alerts.length,
  };

  return (
    <>
      {/* Overlay */}
      <div style={styles.overlay} onClick={onClose} />

      {/* Panel */}
      <div style={styles.panel}>
        {/* Header */}
        <div style={styles.header}>
          <h2 style={styles.headerTitle}>
            <Icons.Bell size={18} />
            {t('alerts.notifications', 'Notifications')}
            {hasTriggered && (
              <span style={styles.headerBadge}>{triggeredAlerts.length}</span>
            )}
          </h2>
          <button style={styles.closeBtn} onClick={onClose} aria-label="Close">
            <Icons.X size={18} />
          </button>
        </div>

        {/* Quick Stats */}
        <div style={styles.quickStats}>
          <div style={styles.stat}>
            <span style={styles.statValue}>{stats.active}</span>
            <span style={styles.statLabel}>{t('alerts.active', 'Actives')}</span>
          </div>
          <div style={styles.statDivider} />
          <div style={styles.stat}>
            <span style={{ ...styles.statValue, color: '#22c55e' }}>{stats.triggered}</span>
            <span style={styles.statLabel}>{t('alerts.triggered', 'Déclenchées')}</span>
          </div>
          <div style={styles.statDivider} />
          <div style={styles.stat}>
            <span style={styles.statValue}>{stats.total}</span>
            <span style={styles.statLabel}>{t('alerts.total', 'Total')}</span>
          </div>
        </div>

        {/* Content */}
        <div style={styles.content}>
          {isLoading ? (
            <div style={styles.emptyState}>
              <Icons.RefreshCw size={24} className="animate-spin" style={{ color: 'var(--primary)' }} />
              <p style={styles.emptyText}>{t('alerts.loading', 'Chargement...')}</p>
            </div>
          ) : allNotifications.length === 0 ? (
            <div style={styles.emptyState}>
              <div style={styles.emptyIcon}>
                <Icons.BellOff size={28} />
              </div>
              <p style={styles.emptyText}>{t('alerts.noNotifications', 'Aucune notification récente')}</p>
              <p style={styles.emptySubtext}>{t('alerts.noNotificationsDesc', 'Vos alertes déclenchées apparaîtront ici')}</p>
            </div>
          ) : (
            <div style={styles.notificationList}>
              {/* Triggered alerts section */}
              {triggeredAlerts.length > 0 && (
                <>
                  <div style={styles.sectionLabel}>
                    <Icons.CheckCircle size={12} />
                    {t('alerts.recentlyTriggered', 'Récemment déclenchées')}
                  </div>
                  {triggeredAlerts.slice(0, 5).map(alert => (
                    <div
                      key={alert.id}
                      style={{
                        ...styles.notificationItem,
                        ...(styles.triggeredItem),
                        ...(hoveredAlertId === alert.id ? styles.hoveredItem : {}),
                      }}
                      onMouseEnter={() => setHoveredAlertId(alert.id)}
                      onMouseLeave={() => setHoveredAlertId(null)}
                    >
                      <div style={styles.notificationIcon}>
                        <Icons.TrendingUp size={16} />
                      </div>
                      <div style={styles.notificationContent}>
                        <div style={styles.notificationHeader}>
                          <span style={styles.notificationToken}>{alert.tokenName}</span>
                          <span style={styles.notificationTime}>{formatDate(alert.triggeredAt || alert.createdAt)}</span>
                        </div>
                        <div style={styles.notificationCondition}>
                          {getConditionText(alert)} {t('alerts.reached', 'atteint')}
                        </div>
                      </div>
                      <button
                        style={styles.dismissBtn}
                        onClick={(e) => handleDismiss(alert.id, e)}
                        title={t('alerts.dismiss', 'Fermer')}
                      >
                        <Icons.X size={14} />
                      </button>
                    </div>
                  ))}
                </>
              )}

              {/* Recent active alerts section */}
              {recentActiveAlerts.length > 0 && (
                <>
                  <div style={styles.sectionLabel}>
                    <Icons.Bell size={12} />
                    {t('alerts.recentlyCreated', 'Récemment créées')}
                  </div>
                  {recentActiveAlerts.map(alert => (
                    <div
                      key={alert.id}
                      style={{
                        ...styles.notificationItem,
                        ...(hoveredAlertId === alert.id ? styles.hoveredItem : {}),
                      }}
                      onMouseEnter={() => setHoveredAlertId(alert.id)}
                      onMouseLeave={() => setHoveredAlertId(null)}
                    >
                      <div style={{ ...styles.notificationIcon, background: 'rgba(59, 130, 246, 0.15)', color: '#60a5fa' }}>
                        <Icons.Bell size={16} />
                      </div>
                      <div style={styles.notificationContent}>
                        <div style={styles.notificationHeader}>
                          <span style={styles.notificationToken}>{alert.tokenName}</span>
                          <span style={styles.notificationTime}>{formatDate(alert.createdAt)}</span>
                        </div>
                        <div style={styles.notificationCondition}>
                          {getConditionText(alert)}
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer - Link to full alerts page */}
        <div style={styles.footer}>
          <button style={styles.viewAllBtn} onClick={handleViewAll}>
            <Icons.Settings size={16} />
            {t('alerts.manageAlerts', 'Gérer mes alertes')}
            <Icons.ChevronRight size={16} />
          </button>
        </div>
      </div>

      {/* CSS animations */}
      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
      `}</style>
    </>
  );
}

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(4px)',
    zIndex: 9998,
  },
  panel: {
    position: 'fixed',
    top: 0,
    right: 0,
    bottom: 0,
    width: '100%',
    maxWidth: '380px',
    background: 'var(--bg)',
    borderLeft: '1px solid var(--border)',
    boxShadow: '-4px 0 24px rgba(0, 0, 0, 0.2)',
    zIndex: 9999,
    display: 'flex',
    flexDirection: 'column',
    animation: 'slideInRight 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    borderBottom: '1px solid var(--border)',
    background: 'var(--card-bg)',
  },
  headerTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    color: 'var(--text)',
    fontSize: '16px',
    fontWeight: 600,
    margin: 0,
  },
  headerBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '20px',
    height: '20px',
    padding: '0 6px',
    background: '#22c55e',
    color: 'white',
    fontSize: '11px',
    fontWeight: 600,
    borderRadius: '10px',
  },
  closeBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    background: 'transparent',
    border: '1px solid var(--border)',
    borderRadius: '8px',
    cursor: 'pointer',
    color: 'var(--muted)',
    transition: 'all 0.2s',
  },
  quickStats: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '20px',
    padding: '14px 20px',
    background: 'var(--card-bg)',
    borderBottom: '1px solid var(--border)',
  },
  stat: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '2px',
  },
  statValue: {
    fontSize: '18px',
    fontWeight: 700,
    color: 'var(--text)',
  },
  statLabel: {
    fontSize: '10px',
    fontWeight: 500,
    color: 'var(--muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  statDivider: {
    width: '1px',
    height: '24px',
    background: 'var(--border)',
  },
  content: {
    flex: 1,
    overflowY: 'auto',
    padding: '12px',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    padding: '40px 20px',
    textAlign: 'center',
  },
  emptyIcon: {
    width: '56px',
    height: '56px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'var(--card-bg)',
    borderRadius: '50%',
    color: 'var(--muted)',
  },
  emptyText: {
    color: 'var(--muted)',
    fontSize: '14px',
    fontWeight: 500,
    margin: 0,
  },
  emptySubtext: {
    color: 'var(--muted)',
    fontSize: '12px',
    margin: 0,
    opacity: 0.7,
  },
  notificationList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  sectionLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '11px',
    fontWeight: 600,
    color: 'var(--muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    padding: '8px 12px 4px',
  },
  notificationItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px',
    background: 'var(--card-bg)',
    border: '1px solid var(--border)',
    borderRadius: '10px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  triggeredItem: {
    background: 'rgba(34, 197, 94, 0.05)',
    borderColor: 'rgba(34, 197, 94, 0.2)',
  },
  hoveredItem: {
    borderColor: 'var(--primary)',
    transform: 'translateX(4px)',
  },
  notificationIcon: {
    width: '36px',
    height: '36px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '10px',
    background: 'rgba(34, 197, 94, 0.15)',
    color: '#22c55e',
    flexShrink: 0,
  },
  notificationContent: {
    flex: 1,
    minWidth: 0,
  },
  notificationHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    marginBottom: '2px',
  },
  notificationToken: {
    fontSize: '14px',
    fontWeight: 600,
    color: 'var(--text)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  notificationTime: {
    fontSize: '11px',
    color: 'var(--muted)',
    flexShrink: 0,
  },
  notificationCondition: {
    fontSize: '12px',
    color: 'var(--muted)',
  },
  dismissBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    background: 'transparent',
    border: 'none',
    borderRadius: '6px',
    color: 'var(--muted)',
    cursor: 'pointer',
    transition: 'all 0.2s',
    opacity: 0.5,
  },
  footer: {
    padding: '12px 16px',
    borderTop: '1px solid var(--border)',
    background: 'var(--card-bg)',
  },
  viewAllBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    width: '100%',
    padding: '12px 16px',
    background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.15), rgba(99, 102, 241, 0.1))',
    border: '1px solid rgba(139, 92, 246, 0.25)',
    borderRadius: '10px',
    color: '#a78bfa',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
};
